import { initContract } from "@ts-rest/core";
import z from "zod";

import {
  documentCategorySchema,
  documentGroupEnumSchema,
  householdMemberDocumentSchema,
  householdMemberDocumentSelectSchema,
} from "@libs/db-schemas";

import { HTTPStatusCode } from "../../types";
import { errorSchema } from "../../utils/errors";
import { householdMemberDocumentRequirementsSchema } from "./schemas";

const c = initContract();

export const uploadedDocumentsContract = c.router({
  generatePresignedUrlForUpload: {
    path: "/uploaded-documents/presigned-upload-url",
    summary: "Generate a presigned URL for uploading a document",
    method: "POST",
    body: z.object({
      householdMemberId: z.uuid(),
      documentCategoryId: z.uuid(),
      files: z
        .object({
          fileName: z.string(),
          contentType: z.string(),
        })
        .array(),
    }),
    responses: {
      [HTTPStatusCode.OK]: z.object({
        documentId: z.uuid(),
        files: z
          .object({
            fileId: z.uuid(),
            url: z.url(),
            objectKey: z.string(),
          })
          .array(),
      }),
      [HTTPStatusCode.BAD_REQUEST]: errorSchema,
    },
  },
  addDocument: {
    path: "/uploaded-documents",
    summary: "Add a new document record",
    method: "POST",
    body: z.object({
      objectKeys: z.string().array(),
    }),
    responses: {
      [HTTPStatusCode.CREATED]: householdMemberDocumentSchema,
      [HTTPStatusCode.BAD_REQUEST]: errorSchema,
    },
  },
  listDocuments: {
    path: "/uploaded-documents",
    summary: "List documents for a household member",
    method: "GET",
    query: z.object({
      householdMemberId: z.uuid(),
      documentGroup: documentGroupEnumSchema.optional(),
    }),
    responses: {
      [HTTPStatusCode.OK]: z.array(
        householdMemberDocumentSelectSchema.omit({ files: true }),
      ),
      [HTTPStatusCode.BAD_REQUEST]: errorSchema,
    },
  },
  getDocument: {
    path: "/uploaded-documents/:id",
    summary: "Get a document by ID",
    method: "GET",
    pathParams: z.object({
      id: z.uuid(),
    }),
    responses: {
      [HTTPStatusCode.OK]: householdMemberDocumentSelectSchema,
      [HTTPStatusCode.NOT_FOUND]: errorSchema,
    },
  },
  deleteDocument: {
    path: "/uploaded-documents/:id",
    summary: "Delete a document by ID",
    method: "DELETE",
    pathParams: z.object({
      id: z.uuid(),
    }),
    responses: {
      [HTTPStatusCode.OK]: c.noBody(),
      [HTTPStatusCode.NOT_FOUND]: errorSchema,
    },
  },
  listDocumentCategories: {
    path: "/uploaded-documents/categories",
    summary: "List all document categories",
    method: "GET",
    responses: {
      [HTTPStatusCode.OK]: documentCategorySchema.array(),
      [HTTPStatusCode.BAD_REQUEST]: errorSchema,
    },
  },
  listDocumentRequirements: {
    path: "/uploaded-documents/requirements",
    summary:
      "List per-household-member document section requirements (visibility + completion). Derived live from the members' application answers and uploaded documents.",
    method: "GET",
    responses: {
      [HTTPStatusCode.OK]: z.array(householdMemberDocumentRequirementsSchema),
      [HTTPStatusCode.BAD_REQUEST]: errorSchema,
    },
  },
});
